import {
  Actions,
  closeDialog,
  FormControl,
  openConfirmDialog,
  openDialog,
  showNotification,
} from 'platform/components';

import {useDispatch} from 'react-redux';

import {head, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  documentContextApi,
  usePostTireSetDeliveryNoteMutation,
  usePostTireSetLabelMutation,
  usePostTiresTransferToOrderMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';

import {Nullish} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {usePermissions} from '../../../hooks/usePermissions/usePermissions';
import {handleApiError} from '../../../utils/handleApiError';
import {printPDF} from '../../../utils/printPDF';
import {DmsDataGrid} from '../../DmsDataGrid/DmsDataGrid';
import {useExcludedTireSetActions} from '../hooks/useExcludedTireSetActions';
import {TireSetDetailFormDefinition} from '../types/TireSetDetailFormDefinition';

interface ExcludedTireSetActionsProps {
  control: FormControl<TireSetDetailFormDefinition>;
  tireOrderId: string;
  tireSetId: string;
  homeBranchId: string | Nullish;
  authorizationProfileId: string | Nullish;
  onClose: VoidFunction;
  onEdit: VoidFunction;
}

export function ExcludedTireSetActions(props: ExcludedTireSetActionsProps) {
  const dispatch = useDispatch();

  const [canEditTireSet] = usePermissions({
    permissionKeys: ['editTireSet'],
    scopes: {
      editTireSet: {
        branchId: props.homeBranchId ?? '',
        authorizationProfileId: props.authorizationProfileId,
      },
    },
  });

  const [postTireSetDeliveryNote, {isLoading: isTiresSetDeliveryNoteLoading}] =
    usePostTireSetDeliveryNoteMutation();

  const [postTireSetLabel, {isLoading: isTiresSetLabelLoading}] = usePostTireSetLabelMutation();

  const [postTiresTransferToOrder] = usePostTiresTransferToOrderMutation();

  const refreshDocumentCount = () =>
    dispatch(
      documentContextApi.util.invalidateTags([{type: 'documentsCount', id: props.tireOrderId}])
    );

  const transferToOrder = () => {
    const transferActionCallback: ActionCallback = ({rowId}) => {
      const targetOrderId = isArray(rowId) ? head(rowId) : rowId;

      if (isNil(targetOrderId)) {
        return;
      }

      openConfirmDialog({
        id: 'tireSetTransferConfirm',
        text: i18n.t('page.tiresInventory.labels.transferToOrderQuestion'),
        onConfirm: () =>
          postTiresTransferToOrder({
            targetOrderId,
            body: {
              tireSets: [{tireOrderId: props.tireOrderId, tireSetId: props.tireSetId}],
            },
          })
            .unwrap()
            .then(() => {
              closeDialog('transferToOrder');
              props.onClose();
              props.onEdit();
            })
            .then(refreshDocumentCount)
            .catch(handleApiError),
      });
    };

    openDialog(
      <DmsDataGrid
        gridCode="tire-set-transfer"
        autoHeight
        actionCallback={transferActionCallback}
      />,
      {
        title: i18n.t('page.tiresInventory.labels.transferToOrder'),
        size: 'large',
        id: 'transferToOrder',
      }
    );
  };

  const printLabel = (selectedWheelIds: string[]) => {
    postTireSetLabel({
      orderId: props.tireOrderId,
      setId: props.tireSetId,
      body: {wheels: selectedWheelIds.map((id) => ({id}))},
    })
      .unwrap()
      .then((data) => {
        const url = data?.printout?.pdfUrl;
        if (url) {
          printPDF(url);
        } else {
          showNotification.error();
        }
      })
      .then(refreshDocumentCount)
      .catch(handleApiError);
  };

  const printDeliveryNote = (selectedWheelIds: string[]) => {
    postTireSetDeliveryNote({
      orderId: props.tireOrderId,
      setId: props.tireSetId,
      body: {wheels: selectedWheelIds.map((id) => ({id}))},
    })
      .unwrap()
      .then((data) => {
        const url = data?.printout?.pdfUrl;
        if (url) {
          printPDF(url);
        } else {
          showNotification.error();
        }
      })
      .then(refreshDocumentCount)
      .catch(handleApiError);
  };

  const excludedActions = useExcludedTireSetActions({
    tireOrderId: props.tireOrderId,
    tireSetId: props.tireSetId,
    control: props.control,
    canEditTireSet,
    isTiresSetLabelLoading,
    isTiresSetDeliveryNoteLoading,
    transferToOrder,
    printLabel,
    printDeliveryNote,
    homeBranchId: props.homeBranchId,
  });

  if (isNil(props.tireSetId)) {
    return null;
  }

  return <Actions actions={excludedActions.actions} />;
}
